import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { theme } from "src/Theme/index";
import { useSelector } from "react-redux";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import ColoredBox from "src/Components/Boxes/TopProductBox/ColoredBox";
import DonutChart from "../StatisticsTest/components/DonutChartPinned";
import { ArrowBackIos } from "@mui/icons-material";
import TopSkus from "./TopSkus";
import HtmlRenderer from "src/Components/shared/shared/HtmlRenderer";
import TopSegmentBrand from "./TopSegmentBrand";

function GapsTopProd({
  hierarchy,
  updateBrand,
  setSku,
  setValue,
  setSummarizedInsights,
  setFullInsights,
}) {
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  // Redux: Get project ID
  const projectId = useSelector((state) => state.userProject.projectId);
  const currentChartType = useSelector((state) => state.chart.currentChartType);

  // Local State
  const [data, setData] = useState([]);
  const [storesData, setStoresData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [valueShare, setValueShare] = useState([]);
  const [volumeSales, setVolumeSales] = useState([]);
  const [donutChatOptions, setDonutChatOptions] = useState({});
  const [donutChatOptions2, setDonutChatOptions2] = useState({});
  const [summarizedInsightsBrand, setSummarizedInsightsBrand] = useState("");
  const [fullInsightsBrand, setFullInsightsBrand] = useState("");
  const [topSkusHierarchy, setTopSkusHierarchy] = useState("");
  const [filter, setFilter] = useState({});

  const [showAdditionalCharts, setShowAdditionalCharts] = useState(false);
  const [availableHierarchies, setAvailableHierarchies] = useState([]);
  const isFreeTrialUser = localStorage.getItem("role") === "free_trial";
  // Styled Table Components
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "3px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: 15,
  }));

  // Fetch API Data
  useEffect(() => {
    if (!projectId) return;

    fetch(`${getUrl()}/market_overview/hierarchy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": localStorage.getItem("userId") || "",
        "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
      },
      body: JSON.stringify({ project_id: projectId }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((result) => {
        const hierarchyData =
          result?.["0"] ||
          result?.Product ||
          result?.product ||
          {};
        const keys = Object.keys(hierarchyData);
        setAvailableHierarchies(keys);
      })
      .catch((e) => {
        console.warn("Failed to load hierarchy keys:", e);
        setAvailableHierarchies([]);
      });
  }, [projectId]);

  useEffect(() => {
    async function fetchTopProducts() {
      if (!projectId) {
        console.warn("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Extract hierarchy and hierarchy_value dynamically
      try {
        console.log(hierarchy)
        const url = `${getUrl()}/market_overview/top_products`;
        const token = localStorage.getItem("userId") || "";
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": token,
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        };
        const payload = {
          project_id: projectId,
          hierarchy,
          opps_filter: true,
          filters: getApiFilters(),
          top_5: true,
          time_period: "P12M",
          target: null,
          token,
        };

        const response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Raw API Response:", result);
        if (!result || Object.keys(result).length === 0) {
          throw new Error("Invalid API response structure.");
        }
        const stores_data = {};

        const names = result.index;

        for (let i = 0; i < names.length; i++) {
          const name = names[i];
          stores_data[name] = {};

          for (const key in result) {
            if (key !== "index") {
              stores_data[name][key] = result[key][i];
            }
          }
        }
        console.log(stores_data)
        setStoresData(stores_data);
        // Transform API response into a usable format
        const formattedData = result["index"].map((name, index) => ({
          name,
          volumeSalesY22: result["Volume_Sales_Previous"]?.[index] || 0,
          volumeSalesY23: result["Volume_Sales_Current"]?.[index] || 0,
          valueSalesY22: result["Value_Sales_Previous"]?.[index] || 0,
          valueSalesY23: result["Value_Sales_Current"]?.[index] || 0,
          valueShareY23: result["Value_Share_Current"]?.[index] || 0,
          dya: (result["Fair_Value_Sales"]?.[index]/1_000_000).toFixed(2) || 0,
          iya: result["IYA"]?.[index] || 0,
          growthIdx: result["Growth_IDX"]?.[index] || 0,
          priceIdx: result["Price_Index_YA"]?.[index] || 0,
          impactOnSales: (result["Impact_on_Sales"]?.[index]/1_000_000) .toFixed(2)|| 0,
          fairValueShare: (result["Fair_Value_Sales"]?.[index]/1_000_000).toFixed(2) || 0,
          valueShareIndex: result["Value_Share_Index"]?.[index] || 0,
          volumeShareIndex: result["Volume_Share_Index"]?.[index] || 0

        }));
        console.log("Transformed data:", formattedData);
        setSummarizedInsights(result["insights_summary"]);
        setFullInsights(result["insights_full"]);
        setData(formattedData);
      } catch (error) {
        console.error("Error fetching top products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTopProducts();
  }, [projectId, currentChartType, selectedFilter, hierarchy]);

  const setupChartData = (value) => {
    setValueShare([
      {
        name: "valueSahre",
        data: [
          ((storesData["Total Market"]["Value_Sales_Current"] -
            storesData[value]["Value_Sales_Current"]) /
            storesData["Total Market"]["Value_Sales_Current"]) *
            100,
          (storesData[value]["Value_Sales_Current"] /
            storesData["Total Market"]["Value_Sales_Current"]) *
            100,
        ],
      },
    ]);
    setVolumeSales([
      {
        name: "volumeSales",
        data: [
          ((storesData["Total Market"]["Volume_Sales_Current"] -
            storesData[value]["Volume_Sales_Current"]) /
            storesData["Total Market"]["Volume_Sales_Current"]) *
            100,
          (storesData[value]["Volume_Sales_Current"] /
            storesData["Total Market"]["Volume_Sales_Current"]) *
            100,
        ],
      },
    ]);

    setDonutChatOptions({
      chart: {
        type: "donut",
      },
      colors: ["#08306B", "#21A0FF"],
      labels: ["Total Market", value],
      title: {
        text: "Value Sales 2024",
        align: "center",
        style: {
          fontSize: "11px",
          fontWeight: "bold",
        },
      },

      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
        fontSize: "13px",
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "30%", // Wider colored section (smaller hole)
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });
    setDonutChatOptions2({
      chart: {
        type: "donut",
      },
      colors: ["#08306B", "#21A0FF"],
      labels: ["Total Market", value],
      title: {
        text: "Volume Sales 2023",
        align: "center",
        style: {
          fontSize: "11px",
          fontWeight: "bold",
        },
      },

      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
        fontSize: "13px",
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}`,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "30%", // Wider colored section (smaller hole)
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    });
  };

  const handleBrandSelection = (value) => {
    setSelectedBrand(value);
    setTopSkusHierarchy(isFreeTrialUser ? "sku_id" : "sku_db");
    const filter = {}
    filter[hierarchy]=value;
    console.log(storesData);
    console.log(value);
    setupChartData(value);
    setFilter(filter);
  };

  const handleTopSkusView = (itemName, targetHierarchy, hierarchy) => {
    setShowAdditionalCharts(true);
    setSelectedBrand(itemName);
    setTopSkusHierarchy(targetHierarchy);
    setupChartData(itemName);
    console.log(itemName);
    console.log(targetHierarchy);
    console.log(hierarchy)
  };

  return (
    <Paper sx={{ minHeight: 200 }}>
      {loading ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : selectedBrand !== "" ? (
        <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {/* Back Button */}
          <Grid item xs={12}>
            <IconButton
              onClick={() => {
                setSelectedBrand("");
                setSku("");
                setValue([]);
                setSummarizedInsightsBrand("");
                setTopSkusHierarchy("");
                setShowAdditionalCharts(false);
              }}
            >
              <ArrowBackIos />
            </IconButton>
          </Grid>

          {/* Brand Name */}
          <Grid item xs={12}>
            <Typography
              data-tour="diag-brand-detail"
              fontWeight={theme.typography.fontWeightBold}
              textAlign="center"
              fontSize={{ xs: "20px", sm: "24px", md: "30px" }}
            >
              {selectedBrand}
            </Typography>
          </Grid>

          {/* Left Column: HTML Insights */}
          <Grid item xs={12} md={6} sx={{ pl: 5 }}>
            {fullInsightsBrand === "" ? (
              <FacebookCircularProgress />
            ) :(
                  <HtmlRenderer htmlString={summarizedInsightsBrand} modalContent={fullInsightsBrand} />
            )
          }
          </Grid>

          {/* Right Column: Two Donut Charts */}
          <Grid item xs={12} md={6} container direction="column" spacing={2}>
            {/* First row of charts */}
            <Grid item container direction="row" spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ maxWidth: { xs: "100%", sm: 300 }, mx: "auto" }}>
                  <DonutChart
                    title="Value Sales 2024"
                    dataValues={valueShare}
                    labels={[{ labels: ["Total Market", selectedBrand] }]}
                    chartOptions={donutChatOptions}
                  />
                </Box>
              </Grid>
              
            </Grid>
            
            {/* Second row of charts */}
            <Grid item container direction="row" spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ maxWidth: { xs: "100%", sm: 300 }, mx: "auto" }}>
                  <DonutChart
                    title="Volume Sales 2024"
                    dataValues={volumeSales}
                    labels={[{ labels: ["Total Market", selectedBrand] }]}
                    chartOptions={donutChatOptions2}
                  />
                </Box>
              </Grid>
              
            </Grid>
          </Grid>

          {/* Bottom Section: Top SKUs */}
          <Grid item xs={12}>
            
            {showAdditionalCharts?
              <TopSegmentBrand
                hierarchy={hierarchy}
                setValue={setValueShare}
                updateBrand={selectedBrand}
                onSkuClick={updateBrand}
                topSkuHier={topSkusHierarchy}
                setSummarized={setSummarizedInsightsBrand}
                setFull={setFullInsightsBrand}
              />

              :
              <TopSkus
              filter={filter}
              hier="sku_db"
              updatedBrand={selectedBrand}
              updateSku={updateBrand}
              setSummarized={setSummarizedInsightsBrand}
              setFull={setFullInsightsBrand}
            />
            }
          </Grid>
        </Grid>
      ) : (
        <>
          <TableContainer
            sx={{
              height: 250,
              overflowY: "auto",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              touchAction: "manipulation",
              backgroundColor: "#fff",
            }}
          >
            <Table stickyHeader aria-label="customized table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Value Share IYA</StyledTableCell>
                  <StyledTableCell>Volume Share IYA</StyledTableCell>
                  <StyledTableCell>Impact on Sales(M SAR)</StyledTableCell>
                  <StyledTableCell>Fair Value Sales (M SAR)</StyledTableCell>
                  <StyledTableCell>Growth IDX</StyledTableCell>
                  <StyledTableCell>Price IDX</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {[
                  data[0],
                  ...data.slice(1).sort((a, b) => Number(a.impactOnSales) - Number(b.impactOnSales)),
                ]
                  .filter(Boolean)
                  .map((item, idx) => {
                  const cellSx = idx === 0 ? { fontWeight: "bold" } : {};

                  return (
                    <StyledTableRow key={idx} sx={idx === 0 ? { height: 40, minHeight: 40 } : {}}>
                      <StyledTableCell sx={cellSx}>{idx + 1}</StyledTableCell>
                      <StyledTableCell sx={cellSx}>{item.name}</StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.valueShareIndex
                        ) : (
                          <ColoredBox
                            number={item.valueShareIndex}
                            Color={
                              item.valueShareIndex > 0
                                ? "#0BB783"
                                : item.valueShareIndex === 0
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.volumeShareIndex
                        ) : (
                          <ColoredBox
                            number={item.volumeShareIndex}
                            Color={
                              item.volumeShareIndex > 0
                                ? "#0BB783"
                                : item.volumeShareIndex === 0
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.impactOnSales
                        ) : (
                          <ColoredBox
                            number={item.impactOnSales}
                            Color={
                              item.impactOnSales > 1
                                ? "#0BB783"
                                : item.impactOnSales === 0
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.fairValueShare
                        ) : (
                          <ColoredBox
                            number={item.fairValueShare}
                            Color={
                              item.fairValueShare > 1
                                ? "#0BB783"
                                : item.fairValueShare === 0
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>

                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.growthIdx
                        ) : (
                          <ColoredBox
                            number={item.growthIdx}
                            Color={
                              item.growthIdx > 1
                                ? "#0BB783"
                                : item.growthIdx === 1
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx === 0 ? (
                          item.priceIdx
                        ) : (
                          <ColoredBox
                            number={item.priceIdx}
                            Color={
                              item.priceIdx > 1
                                ? "#0BB783"
                                : item.priceIdx === 1
                                ? "#0095FF"
                                : "#B22234"
                            }
                          />
                        )}
                      </StyledTableCell>
                      <StyledTableCell sx={cellSx}>
                        {idx !== 0 && (
                          <Box display="flex" gap={1}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleBrandSelection(item.name)}
                            >
                              View All
                            </Button>

                            <span data-tour="diag-view-class">
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  const targetHierarchy =
                                    hierarchy === "brand"
                                      ? isFreeTrialUser &&
                                        (availableHierarchies.includes("segmnt") ||
                                          availableHierarchies.includes("segment"))
                                        ? (availableHierarchies.includes("segmnt")
                                            ? "segmnt"
                                            : "segment")
                                        : "Class"
                                      : "brand";
                                  handleTopSkusView(
                                    item.name,
                                    targetHierarchy,
                                    hierarchy
                                  );
                                }}
                              >
                                {hierarchy === "brand"
                                  ? "View Class"
                                  : "View Brand"}
                              </Button>
                            </span>
                          </Box>
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}
    </Paper>
  );
}

export default GapsTopProd;
